import {MouseEvent, useMemo} from 'react';
import {get} from 'lodash';

import Typography from 'components/Typography';

import {Order, OrderType} from 'types';
import styles from './Ticket.module.scss';
import classNames from 'classnames';
import CircleXIcon from '../../icons/circle-x';
import {PinIcon} from 'pages/DeliveryPlanner/components/Ticket/pin';
import {ChevronDownIcon} from 'pages/RouteHistory/icons/chevron-down';
import {EditIcon} from 'pages/DeliveryPlanner/components/Ticket/edit';
import PriorityFlag from 'components/PriorityFlag';
import StatusBadge from 'components/StatusBadge';
import {getFullName} from 'helpers/user';
import ChainIcon from 'pages/DeliveryPlanner/icons/chain';

interface Props {
  ticket: Order;
  index?: number;
  isAssigned: boolean;
  onChangeCheckbox: (id: number, checked: boolean) => void;
  handleNoteClick: (e: MouseEvent<HTMLDivElement>) => void;
  onDelete?: () => void;
  showTime?: boolean;
  isPrint?: boolean;
  isError?: boolean;
  isCanceled?: boolean;
  signed_at?: string;
  signed_time?: string;
  openedPopup?: {
    index: number;
    open: boolean;
    refKey: string;
  };
  togglePopup?: (index: number, open: boolean, key: string) => void;
}

function naming(time: string) {
  if (typeof time !== 'string') return '';
  const [hour, minute] = time.split(':');
  let hourInt = parseInt(hour, 10);
  let minuteInt = parseInt(minute, 10);

  let period = 'AM';
  if (hourInt >= 12) {
    period = 'PM';
  }

  if (hourInt === 0) {
    hourInt = 12;
  } else if (hourInt > 12) {
    hourInt -= 12;
  }
  return `${hourInt}:${minuteInt.toString().padStart(2, '0')} ${period}`;
}

function Ticket({
  index,
  isAssigned,
  handleNoteClick,
  showTime,
  isPrint = false,
  isError,
  onDelete,
  isCanceled,
  togglePopup,
  openedPopup,
  ticket,
}: Props) {
  const {
    arrival_date,
    arrival_time,
    signed_time,
    signed_at,
    delivery_address,
    customer,
    status,
    orders,
    is_canceled,
    order_type,
  } = ticket;
  const refKey = [
    get(delivery_address, 'latitude', ''),
    get(delivery_address, 'longitude', ''),
  ].join(',');
  const isVendor = customer?.client_role === 'vendors';
  const customerName = isVendor
    ? customer?.customer_company
    : getFullName(customer?.user);
  const isOpen = openedPopup?.refKey === refKey && openedPopup.open;
  const pickup_number = orders?.[0]?.pickup;
  const hasPickup = order_type === 'dropoff' && pickup_number !== null;
  const priorities = useMemo(
    () =>
      orders.filter((order) => !!order.priority).map((order) => order.priority),
    [orders]
  );
  const sequential_order_number = orders[0]?.sequential_order_number;
  const flag_reason = orders[0]?.flag_reason;

  const renderOrderType = (order_type?: OrderType) => {
    if (order_type === 'pickup') {
      return (
        <StatusBadge color="default" className={styles.orderTypeBadge}>
          P{sequential_order_number}
        </StatusBadge>
      );
    }
    if (order_type === 'dropoff') {
      return (
        <StatusBadge color="green" className={styles.orderTypeBadge}>
          D{sequential_order_number}
        </StatusBadge>
      );
    }
    return null;
  };
  return (
    <div
      className={classNames(
        styles.root,
        ((typeof delivery_address !== 'string' &&
          !delivery_address?.is_valid) ||
          isError) &&
          styles.borderColor
      )}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        {isPrint ? (
          <div className={styles.top}>
            <Typography variant="h4">STOP # {index ?? ''}</Typography>
            {typeof arrival_time === 'string' && isAssigned && showTime && (
              <span className={styles.delivery_time}>
                Arrival time - {naming(arrival_time)}
              </span>
            )}
          </div>
        ) : (
          <div style={{marginBottom: 8}} className={styles.leftElement}>
            <Typography variant="label" style={{marginBottom: 0}}>
              #{sequential_order_number}
            </Typography>
            <div className={styles.customerContainer}>
              <Typography
                title={customerName}
                className={classNames(styles.customer, styles.customerEllipsis)}
                variant="h4"
              >
                {customerName}
              </Typography>
              {renderOrderType(order_type)}
            </div>
          </div>
        )}
        {index && !isPrint && (
          <div className={styles.rightElement}>
            {!!priorities.length && (
              <PriorityFlag
                priority={priorities[0] as any}
                flag_reason={flag_reason as string | null}
              />
            )}
            {!!isCanceled ? (
              <>
                <span
                  className={classNames(
                    styles.index,
                    status === 'completed' && styles.completed,
                    !!is_canceled && styles.canceledIndex
                  )}
                >
                  {index}
                </span>

                {status !== 'completed' && !is_canceled && (
                  <div onClick={onDelete} className={styles.close_icon}>
                    <CircleXIcon width={18} height={18} />
                  </div>
                )}
              </>
            ) : (
              <span className={styles.canceled}>Canceled</span>
            )}
            <div
              onClick={() => {
                togglePopup && togglePopup(index, !isOpen, refKey);
              }}
            >
              <ChevronDownIcon
                className={classNames(isOpen && styles.open)}
                width={18}
                height={18}
              />
            </div>
          </div>
        )}
      </div>
      {!isCanceled && !isPrint && (
        <span className={styles.canceled}>Skipped</span>
      )}

      {(isOpen || isPrint) && (
        <>
          {hasPickup && (
            <div className={styles.pickupContainer}>
              <ChainIcon width={12} height={12} className={styles.pinIcon} />
              <Typography className={styles.pickupText} variant="p">
                Associated Pickup: #{pickup_number}
              </Typography>
            </div>
          )}
          {typeof delivery_address === 'string' ? (
            <div className={styles.addressContainer}>
              <PinIcon className={styles.pinIcon} />
              <Typography className={styles.addressCalc} variant="p">
                {delivery_address}
              </Typography>
            </div>
          ) : (
            <div className={styles.addressContainer}>
              <PinIcon className={styles.pinIcon} />
              {delivery_address?.display_address && (
                <Typography className={styles.address} variant="p">
                  {delivery_address?.display_address}
                </Typography>
              )}
              {!delivery_address?.display_address && (
                <Typography className={styles.address} variant="p">
                  {[
                    delivery_address?.address_line_1,
                    delivery_address?.address_line_2,
                    delivery_address?.city,
                    delivery_address?.state,
                    delivery_address?.zipcode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              )}
            </div>
          )}
          <div className={styles.footer}>
            {typeof arrival_time === 'string' &&
              isAssigned &&
              showTime &&
              !isPrint && (
                <div className={styles.delivery_times_container}>
                  <div className={styles.delivery_time_container}>
                    <Typography
                      variant="label"
                      className={styles.delivery_time}
                    >
                      Est. time:
                    </Typography>
                    <Typography
                      variant="label"
                      className={styles.delivery_time}
                    >
                      {arrival_date ? `${arrival_date}  ` : ''}{' '}
                      {naming(arrival_time)}
                    </Typography>
                  </div>
                  {!!signed_at && (
                    <div className={styles.delivery_time_container}>
                      <Typography
                        variant="label"
                        className={styles.delivery_time}
                      >
                        Delivered Time
                      </Typography>
                      <Typography
                        variant="label"
                        className={styles.delivery_time}
                      >
                        {signed_at?.split('/')?.[0]?.replace('0', '') +
                          '/' +
                          signed_at?.split('/')?.[1] +
                          ' ' +
                          signed_at?.split(' ')?.[1] +
                          ' ' +
                          (signed_time?.startsWith('0')
                            ? signed_time?.slice(1)
                            : signed_time)}
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            <div className={styles.editIcon} onClick={handleNoteClick}>
              <EditIcon width={13} height={13} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Ticket;
